import clsx from 'clsx';
import { useMemo } from 'react';
import { LuDraftingCompass, LuMoon, LuSun } from 'react-icons/lu';

import { Avatar, AvatarFallback, AvatarImage } from '~components/ui/avatar';
import { Button } from '~components/ui/button';
import { APP_NAME } from '~config';
import { ChangelogSheet } from '~features/changelog';
import { useUserProfile } from '~hooks';
import { useThemeStore } from '~store';
import { getInitials } from '~utils';
import { Breadcrumb } from './Breadcrumb';

/**
 * ℹ️ While we could use React.memo for ThemeSwitch, it is unnecessary
 * because the component is already optimized via Zustand's selective subscription.
 */
const ThemeSwitch = () => {
  const theme = useThemeStore((state) => state.theme);
  const setTheme = useThemeStore((state) => state.setTheme);

  const isDark = theme === 'dark';

  return (
    <Button
      icon
      size="sm"
      variant="ghost"
      onClick={() => setTheme(isDark ? 'light' : 'dark')}
      aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}
      title={`Switch to ${isDark ? 'light' : 'dark'} mode`}
      type="button"
    >
      <LuMoon
        className={clsx('size-6', { hidden: isDark })}
        fill="var(--color-primary)"
        color="var(--color-primary)"
        aria-hidden
      />
      <LuSun className={clsx('size-6', { hidden: !isDark })} aria-hidden />
    </Button>
  );
};
ThemeSwitch.displayName = 'ThemeSwitch';

/**
 * Application navigation bar displaying the app logo, name,
 * theme toggle, and authenticated user profile info.
 */
const Navbar = () => {
  const { photo, details } = useUserProfile();

  const initials = useMemo(() => getInitials(details?.displayName ?? ''), [details?.displayName]);

  return (
    <header className="flex items-center justify-between border-border border-b bg-background px-6 py-3">
      {/* Left Content */}
      <div className="flex items-center gap-4">
        <div className="flex grow basis-0 items-center gap-4 border-border border-r-2">
          <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary">
            <LuDraftingCompass className="size-6 text-primary-foreground" aria-hidden />
          </div>
          <h2 className="hidden pr-4 font-bold text-foreground text-lg sm:block">{APP_NAME}</h2>
        </div>
        {/* Breadcrumb */}
        <Breadcrumb />
      </div>

      {/* Right Content */}
      <div className="flex shrink-0 items-center justify-end gap-4">
        <ChangelogSheet />

        <ThemeSwitch />

        {/* User Account */}
        <div className="flex items-center gap-3 border-border border-l-2 pl-4">
          <div className="hidden text-end sm:block">
            <div className="font-bold text-sm">{details?.displayName ?? 'User'}</div>
            {details?.jobTitle && <div className="text-xs">{details.jobTitle}</div>}
          </div>
          <Avatar className="size-[38px]">
            {photo?.dataUrl && (
              <AvatarImage src={photo.dataUrl} alt={`${details?.displayName ?? 'User'} profile photo`} />
            )}
            <AvatarFallback className="text-[1rem]">{initials}</AvatarFallback>
          </Avatar>
        </div>
      </div>
    </header>
  );
};
Navbar.displayName = 'Navbar';

export { Navbar };
